import { ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button, { ButtonProps } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

/** A third button beside cancel and confirm, e.g. "ปิดสินค้าแทน" in a delete dialog. */
export interface ConfirmModalSecondaryAction {
  label: string;
  onClick: () => void;
  /** Spins this button, and locks the others, while its own request runs. */
  loading?: boolean;
}

interface ConfirmModalProps {
  title: string;
  description?: string;
  /** Extra body rendered under the description, e.g. a consequence list or an input. */
  children?: ReactNode;
  open: boolean;
  loading?: boolean;
  /** Adds a ✕ in the top-right corner that closes the dialog like the cancel button. */
  showCloseButton?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: ButtonProps["color"];
  confirmDisabled?: boolean;
  secondaryAction?: ConfirmModalSecondaryAction;
  onClose: () => void;
  onConfirm: () => void;
}

export default function ConfirmModal({
  title,
  description,
  children,
  open,
  loading,
  showCloseButton = false,
  confirmLabel = "ยืนยัน",
  cancelLabel = "ยกเลิก",
  confirmColor = "primary",
  confirmDisabled = false,
  secondaryAction,
  onClose,
  onConfirm,
}: ConfirmModalProps) {
  // Either request in flight locks every way out, so a second action cannot race it.
  const isBusy = Boolean(loading || secondaryAction?.loading);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={showCloseButton ? { pr: 6 } : undefined}>
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="ปิด"
            disabled={isBusy}
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {description && <DialogContentText>{description}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions>
        <Button disabled={isBusy} onClick={onClose}>
          {cancelLabel}
        </Button>
        {secondaryAction && (
          <Button
            variant="outlined"
            loading={secondaryAction.loading}
            disabled={isBusy}
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button
          loading={loading}
          variant="contained"
          color={confirmColor}
          disabled={confirmDisabled || isBusy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
